/** Set on the frozen question-area element; the annotation cursor CSS keys off it. */
export const ANNOTATION_FROZEN_ATTRIBUTE = 'data-annotation-frozen';

/** Marks a subtree the shield leaves interactive (navigation/submit, pending F4). */
export const ANNOTATION_SHIELD_EXEMPT_ATTRIBUTE = 'data-annotation-shield-exempt';

export const ANNOTATION_SHIELD_EXEMPT_PROPS = {[ANNOTATION_SHIELD_EXEMPT_ATTRIBUTE]: ''} as const;

/** Whether the event targets a subtree the shield leaves interactive. */
export function isShieldExempt(event: {target: EventTarget | null}): boolean {
  return (
    event.target instanceof Element &&
    event.target.closest(`[${ANNOTATION_SHIELD_EXEMPT_ATTRIBUTE}]`) !== null
  );
}

function blockPropagation(event: Event): void {
  if (isShieldExempt(event)) {
    return;
  }
  event.stopPropagation();
}

function blockPropagationAndDefault(event: Event): void {
  if (isShieldExempt(event)) {
    return;
  }
  event.stopPropagation();
  event.preventDefault();
}

// Tab: no focus trap; Escape: the popover's dismiss listens on document.
const PASS_THROUGH_KEYS = new Set(['Tab', 'Escape']);

function blockKey(event: Event): void {
  if (
    isShieldExempt(event) ||
    (event instanceof KeyboardEvent && PASS_THROUGH_KEYS.has(event.key))
  ) {
    return;
  }
  event.stopPropagation();
}

// Never cancelled, so declared passive — keeps native scrolling on the fast path.
const PASSIVE_EVENTS = new Set(['touchstart', 'wheel']);

/**
 * preventDefault only where the default action itself answers; plain stops keep
 * selection, focus, and scrolling alive. `mouseup` is absent — annotation capture.
 */
const SHIELDED_EVENTS: ReadonlyArray<[string, EventListener]> = [
  ['click', blockPropagationAndDefault],
  ['dblclick', blockPropagation],
  ['mousedown', blockPropagation],
  ['pointerdown', blockPropagation],
  ['touchstart', blockPropagation],
  ['keydown', blockKey],
  ['keypress', blockKey],
  ['keyup', blockKey],
  ['beforeinput', blockPropagationAndDefault],
  // insertCompositionText beforeinput isn't cancelable; cancel the IME session at start.
  ['compositionstart', blockPropagationAndDefault],
  ['paste', blockPropagationAndDefault],
  ['cut', blockPropagationAndDefault],
  ['drop', blockPropagationAndDefault],
  ['submit', blockPropagationAndDefault],
  ['wheel', blockPropagation]
];

/** Capture-phase by construction: descendant and document-level handlers never fire first. */
export function attachShield(element: HTMLElement): void {
  SHIELDED_EVENTS.forEach(([type, listener]) => {
    element.addEventListener(type, listener, {capture: true, passive: PASSIVE_EVENTS.has(type)});
  });
}

export function detachShield(element: HTMLElement): void {
  SHIELDED_EVENTS.forEach(([type, listener]) => {
    element.removeEventListener(type, listener, {capture: true});
  });
}
